import TeamMetricsAPI from '../../api/teamMetrics';
import {
  apiParamsWithoutCacheControl,
  readCrmCache,
  shouldBypassCrmCache,
  writeCrmCache,
} from '../../helper/crmCache';

const CACHE_TTL = 5 * 60 * 1000;

const MUTATIONS = {
  SET_SUMMARY: 'TEAM_METRICS_SET_SUMMARY',
  SET_SUPERVISORS: 'TEAM_METRICS_SET_SUPERVISORS',
  SET_AGGREGATES: 'TEAM_METRICS_SET_AGGREGATES',
  SET_ALERTS: 'TEAM_METRICS_SET_ALERTS',
  SET_CONSULTANTS: 'TEAM_METRICS_SET_CONSULTANTS',
  SET_AVAILABLE_TEAMS: 'TEAM_METRICS_SET_AVAILABLE_TEAMS',
  START_FETCHING: 'TEAM_METRICS_START_FETCHING',
  FINISH_FETCHING: 'TEAM_METRICS_FINISH_FETCHING',
};

const state = {
  summary: [],
  supervisors: [],
  aggregates: {},
  alerts: {},
  consultants: [],
  availableTeams: [],
  uiFlags: {
    isFetching: false,
    pendingRequests: 0,
  },
};

const getters = {
  getTeamSummary: $state => $state.summary,
  getTeamSupervisors: $state => $state.supervisors,
  getAggregates: $state => $state.aggregates,
  getAlerts: $state => $state.alerts,
  getTeamConsultants: $state => $state.consultants,
  getTeamAvailableTeams: $state => $state.availableTeams,
  getTeamUIFlags: $state => $state.uiFlags,
};

const actions = {
  fetchTeamSummary: async ({ commit }, params = {}) => {
    const apiParams = apiParamsWithoutCacheControl(params);
    const cached = shouldBypassCrmCache(params)
      ? null
      : readCrmCache('teamMetrics.summary', apiParams, CACHE_TTL);

    if (cached) {
      commit(MUTATIONS.SET_SUMMARY, cached);
      return;
    }

    commit(MUTATIONS.START_FETCHING);
    try {
      const { data } = await TeamMetricsAPI.getSummary(apiParams);
      const summary = data.summary ?? [];
      commit(MUTATIONS.SET_SUMMARY, summary);
      writeCrmCache('teamMetrics.summary', apiParams, summary);
    } catch {
      commit(MUTATIONS.SET_SUMMARY, []);
    } finally {
      commit(MUTATIONS.FINISH_FETCHING);
    }
  },

  fetchTeamSupervisors: async ({ commit }, params = {}) => {
    const apiParams = apiParamsWithoutCacheControl(params);
    const cached = shouldBypassCrmCache(params)
      ? null
      : readCrmCache('teamMetrics.supervisors', apiParams, CACHE_TTL);

    if (cached) {
      commit(MUTATIONS.SET_SUPERVISORS, cached.supervisors ?? []);
      commit(MUTATIONS.SET_AGGREGATES, cached.aggregates ?? {});
      commit(MUTATIONS.SET_ALERTS, cached.alerts ?? {});
      return;
    }

    commit(MUTATIONS.START_FETCHING);
    try {
      const { data } = await TeamMetricsAPI.getSupervisors(apiParams);
      const payload = {
        supervisors: data.supervisors ?? [],
        aggregates: data.aggregates ?? {},
        alerts: data.alerts ?? {},
      };
      commit(MUTATIONS.SET_SUPERVISORS, payload.supervisors);
      commit(MUTATIONS.SET_AGGREGATES, payload.aggregates);
      commit(MUTATIONS.SET_ALERTS, payload.alerts);
      writeCrmCache('teamMetrics.supervisors', apiParams, payload);
    } catch {
      commit(MUTATIONS.SET_SUPERVISORS, []);
      commit(MUTATIONS.SET_AGGREGATES, {});
      commit(MUTATIONS.SET_ALERTS, {});
    } finally {
      commit(MUTATIONS.FINISH_FETCHING);
    }
  },

  fetchTeamConsultants: async ({ commit }, params = {}) => {
    const apiParams = apiParamsWithoutCacheControl(params);
    const cached = shouldBypassCrmCache(params)
      ? null
      : readCrmCache('teamMetrics.consultants', apiParams, CACHE_TTL);

    if (cached) {
      commit(MUTATIONS.SET_CONSULTANTS, cached);
      return;
    }

    commit(MUTATIONS.START_FETCHING);
    try {
      const { data } = await TeamMetricsAPI.getConsultants(apiParams);
      const consultants = data.consultants ?? [];
      commit(MUTATIONS.SET_CONSULTANTS, consultants);
      writeCrmCache('teamMetrics.consultants', apiParams, consultants);
    } catch {
      commit(MUTATIONS.SET_CONSULTANTS, []);
    } finally {
      commit(MUTATIONS.FINISH_FETCHING);
    }
  },

  fetchTeamAvailableTeams: async ({ commit }, params = {}) => {
    const cached = shouldBypassCrmCache(params)
      ? null
      : readCrmCache('teamMetrics.availableTeams', {}, CACHE_TTL);

    if (cached) {
      commit(MUTATIONS.SET_AVAILABLE_TEAMS, cached);
      return;
    }

    try {
      const { data } = await TeamMetricsAPI.getAvailableTeams();
      const teams = data.teams ?? [];
      commit(MUTATIONS.SET_AVAILABLE_TEAMS, teams);
      writeCrmCache('teamMetrics.availableTeams', {}, teams);
    } catch {
      // silently ignore — dropdown stays empty
    }
  },
};

const mutations = {
  [MUTATIONS.SET_SUMMARY]($state, data) {
    $state.summary = data;
  },
  [MUTATIONS.SET_SUPERVISORS]($state, data) {
    $state.supervisors = data;
  },
  [MUTATIONS.SET_AGGREGATES]($state, data) {
    $state.aggregates = data;
  },
  [MUTATIONS.SET_ALERTS]($state, data) {
    $state.alerts = data;
  },
  [MUTATIONS.SET_CONSULTANTS]($state, data) {
    $state.consultants = data;
  },
  [MUTATIONS.SET_AVAILABLE_TEAMS]($state, data) {
    $state.availableTeams = data;
  },
  [MUTATIONS.START_FETCHING]($state) {
    $state.uiFlags.pendingRequests += 1;
    $state.uiFlags.isFetching = true;
  },
  [MUTATIONS.FINISH_FETCHING]($state) {
    $state.uiFlags.pendingRequests = Math.max(
      0,
      $state.uiFlags.pendingRequests - 1
    );
    $state.uiFlags.isFetching = $state.uiFlags.pendingRequests > 0;
  },
};

export default { state, getters, actions, mutations };
